import { useState } from "react";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface HairAreaSelector2DProps {
  selectedAreas: string[];
  onChange: (area: string, isSelected: boolean) => void;
  /** Shown when `other` is selected in {@link selectedAreas}. */
  otherText?: string;
  onOtherTextChange?: (value: string) => void;
  className?: string;
}

// Colors extracted strictly from the reference image
const COLORS = {
  skin: "#F5CBA7", // Warm peach
  skinShadow: "#E59866", // Darker peach for neck shadow
  beard: "#5D4037", // Dark Brown beard
  
  // Hair Zones matching the image
  temples: "#1A2E40", // Dark Navy (Zone 1)
  hairline: "#143D36", // Dark Forest Green (Zone 2)
  frontal: "#7D9C7D", // Olive/Sage Green (Zone 3)
  mid1: "#6E4C3B", // Dark Brown (Zone 4)
  mid2: "#A67C52", // Medium Brown (Zone 5)
  crown: "#C2A884", // Tan/Beige (Zone 6)

  other: "#78909C", // Blue gray — grid-only option (no diagram zone)

  selected: "#166B93", // Active selection color
};

/** Lingui messages for grid cards (diagram uses same ids). */
const HAIR_AREA_MSG = {
  temples: { label: msg`Temples`, description: msg`Side triangles` },
  hairline: { label: msg`Hairline`, description: msg`Lower front band` },
  frontal_scalp: { label: msg`Frontal Scalp`, description: msg`Front center` },
  midscalp_i: { label: msg`Mid-scalp I`, description: msg`Lower middle` },
  midscalp_ii: { label: msg`Mid-scalp II`, description: msg`Upper middle` },
  crown: { label: msg`Crown`, description: msg`Vertex/Top` },
  beard: { label: msg`Beard`, description: msg`Facial hair` },
  other: { label: msg`Other`, description: msg`Describe below` },
} as const;

const HAIR_AREA_ORDER: {
  id: keyof typeof HAIR_AREA_MSG;
  number: string;
  color: string;
}[] = [
  { id: "temples", number: "1", color: COLORS.temples },
  { id: "hairline", number: "2", color: COLORS.hairline },
  { id: "frontal_scalp", number: "3", color: COLORS.frontal },
  { id: "midscalp_i", number: "4", color: COLORS.mid1 },
  { id: "midscalp_ii", number: "5", color: COLORS.mid2 },
  { id: "crown", number: "6", color: COLORS.crown },
  { id: "beard", number: "7", color: COLORS.beard },
  { id: "other", number: "8", color: COLORS.other },
];

export default function HairAreaSelector2D({
  selectedAreas,
  onChange,
  otherText = "",
  onOtherTextChange,
  className,
}: HairAreaSelector2DProps) {
  const { i18n } = useLingui();
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);

  const handleAreaClick = (areaId: string) => {
    const isSelected = selectedAreas.includes(areaId);
    onChange(areaId, !isSelected);
  };

  const isSelected = (areaId: string) => selectedAreas.includes(areaId);
  const isHovered = (areaId: string) => hoveredArea === areaId;

  const getAreaColor = (areaId: string) => {
    if (isSelected(areaId)) return COLORS.selected;
    return HAIR_AREA_ORDER.find((a) => a.id === areaId)?.color || "#ccc";
  };

  const getOpacity = (areaId: string) => {
    if (isSelected(areaId)) return 1;
    if (isHovered(areaId)) return 0.9;
    return 1;
  };

  return (
    <div className={cn("relative w-full max-w-full min-w-0 mx-auto", className)}>
      {/* Removed inner card styling (bg-gray-50, border, shadow) to prevent "card inside card" look */}
      <div className="w-full min-w-0 overflow-x-hidden">
        <div className="relative mx-auto flex w-full min-w-0 flex-col items-center">
          {/* Vector Illustration - Responsive Sizing */}
          <div className="w-full max-w-[220px] shrink-0 sm:max-w-[280px]">
            <svg viewBox="0 30 360 370" className="w-full max-w-full h-auto drop-shadow-xl" style={{ overflow: "hidden" }}>
              <defs>
                <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.1" />
                </filter>
                
                {/* CLIP PATH: Head Shape */}
                <clipPath id="headClip">
                   <path d="M 60 180 
                       C 60 80, 300 80, 300 180 
                       L 300 230 
                       Q 300 290, 270 330
                       Q 240 370, 180 370
                       Q 120 370, 90 330
                       Q 60 290, 60 230
                       Z" />
                </clipPath>
              </defs>
              
              {/* SHOULDERS / CLOTHING */}
              <path d="M 20 400 Q 90 360 180 360 Q 270 360 340 400 V 420 H 20 Z" fill="#EAECEE" />

              {/* NECK */}
              <path d="M 115 330 L 115 380 Q 180 395 245 380 L 245 330" fill="#D6A284" />

              {/* HEAD BASE */}
              <path d="M 60 180 
                       C 60 80, 300 80, 300 180 
                       L 300 230 
                       Q 300 290, 270 330
                       Q 240 370, 180 370
                       Q 120 370, 90 330
                       Q 60 290, 60 230
                       Z" 
                    fill={COLORS.skin} />

              {/* EARS - Closer to head */}
              <path d="M 60 210 Q 40 220 40 245 Q 40 270 60 280" fill={COLORS.skin} />
              <path d="M 50 230 Q 45 250 55 270" stroke="#D6A284" strokeWidth="2" fill="none" opacity="0.5" />
              
              <path d="M 300 210 Q 320 220 320 245 Q 320 270 300 280" fill={COLORS.skin} />
              <path d="M 310 230 Q 315 250 305 270" stroke="#D6A284" strokeWidth="2" fill="none" opacity="0.5" />


              {/* --- HAIR ZONES --- */}
              <g clipPath="url(#headClip)">
              
                {/* 6. CROWN (Tan) */}
                <ellipse 
                  cx="180" cy="90" rx="70" ry="55"
                  fill={getAreaColor("crown")}
                  fillOpacity={getOpacity("crown")}
                  className="cursor-pointer transition-colors duration-200"
                  onClick={() => handleAreaClick("crown")}
                  onMouseEnter={() => setHoveredArea("crown")}
                  onMouseLeave={() => setHoveredArea(null)}
                />
                
                {/* 5. MID SCALP II (Medium Brown) */}
                <path 
                  d="M 60 145 Q 180 125 300 145 L 300 175 Q 180 155 60 175 Z"
                  fill={getAreaColor("midscalp_ii")}
                  fillOpacity={getOpacity("midscalp_ii")}
                  className="cursor-pointer transition-colors duration-200"
                  onClick={() => handleAreaClick("midscalp_ii")}
                  onMouseEnter={() => setHoveredArea("midscalp_ii")}
                  onMouseLeave={() => setHoveredArea(null)}
                />

                {/* 4. MID SCALP I (Dark Brown) */}
                <path 
                  d="M 60 175 Q 180 155 300 175 L 300 205 Q 180 185 60 205 Z"
                  fill={getAreaColor("midscalp_i")}
                  fillOpacity={getOpacity("midscalp_i")}
                  className="cursor-pointer transition-colors duration-200"
                  onClick={() => handleAreaClick("midscalp_i")}
                  onMouseEnter={() => setHoveredArea("midscalp_i")}
                  onMouseLeave={() => setHoveredArea(null)}
                />

                {/* 3. FRONTAL (Olive Green) */}
                <path 
                  d="M 125 200 L 235 200 L 230 235 Q 180 250 130 235 Z"
                  fill={getAreaColor("frontal_scalp")}
                  fillOpacity={getOpacity("frontal_scalp")}
                  className="cursor-pointer transition-colors duration-200"
                  onClick={() => handleAreaClick("frontal_scalp")}
                  onMouseEnter={() => setHoveredArea("frontal_scalp")}
                  onMouseLeave={() => setHoveredArea(null)}
                />

                {/* 2. HAIRLINE (Dark Green) */}
                <path 
                  d="M 95 230 Q 180 260 265 230 L 255 250 Q 180 270 105 250 Z"
                  fill={getAreaColor("hairline")}
                  fillOpacity={getOpacity("hairline")}
                  className="cursor-pointer transition-colors duration-200"
                  onClick={() => handleAreaClick("hairline")}
                  onMouseEnter={() => setHoveredArea("hairline")}
                  onMouseLeave={() => setHoveredArea(null)}
                />

                {/* 1. TEMPLES (Navy) */}
                <path 
                  d="M 50 195 L 125 200 L 130 235 L 105 250 L 58 262 Z" // Left
                  fill={getAreaColor("temples")}
                  fillOpacity={getOpacity("temples")}
                  className="cursor-pointer transition-colors duration-200"
                  onClick={() => handleAreaClick("temples")}
                  onMouseEnter={() => setHoveredArea("temples")}
                  onMouseLeave={() => setHoveredArea(null)}
                />
                <path 
                  d="M 310 195 L 235 200 L 230 235 L 255 250 L 302 262 Z" // Right
                  fill={getAreaColor("temples")}
                  fillOpacity={getOpacity("temples")}
                  className="cursor-pointer transition-colors duration-200"
                  onClick={() => handleAreaClick("temples")}
                  onMouseEnter={() => setHoveredArea("temples")}
                  onMouseLeave={() => setHoveredArea(null)}
                />

                {/* 7. BEARD */}
                <path 
                  d="M 50 260 
                     L 50 390 
                     L 310 390 
                     L 310 260
                     C 280 270, 250 320, 220 350
                     Q 180 375, 140 350
                     C 110 320, 80 270, 50 260 Z
                     M 140 350 
                     Q 180 345, 220 350 
                     Q 180 353, 140 350 Z"
                  fill={getAreaColor("beard")}
                  fillOpacity={getOpacity("beard")}
                  className="cursor-pointer transition-colors duration-200"
                  onClick={() => handleAreaClick("beard")}
                  onMouseEnter={() => setHoveredArea("beard")}
                  onMouseLeave={() => setHoveredArea(null)}
                />

              </g>

              {/* NUMBERS */}
              <g style={{ filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.2))' }}>
                  <text x="180" y="95" textAnchor="middle" fill="white" fontWeight="600" fontSize="18" pointerEvents="none">6</text>
                  <text x="180" y="152" textAnchor="middle" fill="white" fontWeight="600" fontSize="16" pointerEvents="none">5</text>
                  <text x="180" y="185" textAnchor="middle" fill="white" fontWeight="600" fontSize="16" pointerEvents="none">4</text>
                  <text x="180" y="222" textAnchor="middle" fill="white" fontWeight="600" fontSize="16" pointerEvents="none">3</text>
                  <text x="180" y="255" textAnchor="middle" fill="white" fontWeight="600" fontSize="12" pointerEvents="none">2</text>
                  <text x="80" y="235" textAnchor="middle" fill="white" fontWeight="600" fontSize="16" pointerEvents="none">1</text>
                  <text x="280" y="235" textAnchor="middle" fill="white" fontWeight="600" fontSize="16" pointerEvents="none">1</text>
                  <text x="100" y="320" textAnchor="middle" fill="white" fontWeight="600" fontSize="16" pointerEvents="none">7</text>
                  <text x="260" y="320" textAnchor="middle" fill="white" fontWeight="600" fontSize="16" pointerEvents="none">7</text>
              </g>

              {/* FACE DETAILS - Matches Reference Vector Style */}
              
              {/* Eyebrows (Thick, Dark Brown) */}
              <path d="M 120 275 Q 145 270 175 280 L 172 288 Q 145 280 120 282 Z" fill={COLORS.beard} />
              <path d="M 240 275 Q 215 270 185 280 L 188 288 Q 215 280 240 282 Z" fill={COLORS.beard} />

              {/* Eyes (Closed curves with creases) */}
              <path d="M 130 300 Q 150 305 170 300" fill="none" stroke="#5D4037" strokeWidth="2.5" strokeLinecap="round" />
              <path d="M 190 300 Q 210 305 230 300" fill="none" stroke="#5D4037" strokeWidth="2.5" strokeLinecap="round" />
              
              {/* Nose (Defined bridge and nostrils) */}
              <path d="M 180 300 L 180 335 Q 180 345 170 340" fill="none" stroke="#D6A284" strokeWidth="2" strokeLinecap="round" /> {/* Bridge Shadow */}
              <path d="M 168 340 Q 180 348 192 340" fill="none" stroke="#C08868" strokeWidth="2.5" strokeLinecap="round" /> {/* Nose Tip */}

              {/* Mouth */}
              <path d="M 160 355 Q 180 365 200 355" fill="none" stroke="#C08868" strokeWidth="2" strokeLinecap="round" />

            </svg>
          </div>
        </div>

        {/* Legend / Cards — class hair-area-selector-grid: mobile index.css resets button margins/min-width that break the grid. */}
        <div
          className={cn(
            "hair-area-selector-grid mt-4 grid w-full min-w-0 justify-items-stretch gap-x-2 gap-y-2 sm:mt-5 sm:gap-x-2 sm:gap-y-2",
            "grid-cols-[repeat(2,minmax(0,1fr))] sm:grid-cols-[repeat(3,minmax(0,1fr))]",
          )}
        >
          {HAIR_AREA_ORDER.map((area) => (
            <button
              key={area.id}
              type="button"
              onClick={() => handleAreaClick(area.id)}
              aria-pressed={isSelected(area.id)}
              className={cn(
                "box-border flex w-full max-w-full min-w-0 flex-nowrap items-center gap-2 self-stretch px-2 py-3 text-left transition-colors duration-200 sm:gap-2.5 sm:px-2.5",
                "rounded-xl border-2",
                isSelected(area.id)
                  ? "border-[#166B93] bg-[#166B93] text-white shadow-sm"
                  : "border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm",
              )}
            >
              <div
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold",
                  isSelected(area.id) ? "bg-white/20 text-white" : "text-white",
                )}
                style={{ backgroundColor: isSelected(area.id) ? undefined : area.color }}
              >
                {area.number}
              </div>
              <div className="min-w-0 flex-1 text-left">
                <div className="text-[13px] font-semibold leading-tight sm:text-sm">
                  {i18n._(HAIR_AREA_MSG[area.id].label)}
                </div>
                <div
                  className={cn(
                    "mt-0.5 text-[11px] leading-tight",
                    isSelected(area.id) ? "text-slate-300" : "text-gray-400",
                  )}
                >
                  {i18n._(HAIR_AREA_MSG[area.id].description)}
                </div>
              </div>
              <span
                className="inline-flex h-4 w-4 shrink-0 items-center justify-center"
                aria-hidden
              >
                {isSelected(area.id) ? (
                  <Check className="h-4 w-4 text-white" strokeWidth={2.5} />
                ) : null}
              </span>
            </button>
          ))}
        </div>
      </div>

        {selectedAreas.includes("other") && onOtherTextChange ? (
          <div className="hair-treatment-area-other mt-4 w-full min-w-0 space-y-2 px-0.5">
            <Label htmlFor="hair-treatment-area-other" className="text-sm font-medium text-gray-800">
              {i18n._(msg`Describe the area or treatment you have in mind`)}
            </Label>
            <Textarea
              id="hair-treatment-area-other"
              value={otherText}
              onChange={(e) => onOtherTextChange(e.target.value)}
              placeholder={i18n._(msg`e.g. eyebrows, scar revision, sideburns…`)}
              className="min-h-[100px] w-full min-w-0 resize-y pt-3.5"
              maxLength={2000}
            />
          </div>
        ) : null}
    </div>
  );
}
